import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import VendorLayout from '@/components/layouts/VendorLayout';
import {
  ArrowLeft, Clock, CheckCircle, AlertCircle,
  Loader2, FileText, Building2, Mail, Tag, Calendar,
  TrendingUp, ThumbsUp, ThumbsDown
} from 'lucide-react';
import { getApiUrl } from '@/lib/api';

const STATUS_CONFIG: Record<string, { label: string; color: string; icon: React.ComponentType<{ className?: string }> }> = {
  pending: { label: 'Pending', color: 'bg-violet-100 text-violet-700', icon: Clock },
  ai_evaluated: { label: 'AI Evaluated', color: 'bg-blue-100 text-blue-700', icon: AlertCircle },
  reviewed: { label: 'Reviewed', color: 'bg-purple-100 text-purple-700', icon: CheckCircle },
  delivered: { label: 'Delivered', color: 'bg-emerald-100 text-emerald-700', icon: CheckCircle },
};

const RECOMMENDATION_CONFIG: Record<string, { label: string; color: string; icon: React.ComponentType<{ className?: string }>; desc: string }> = {
  pursue: { label: 'Pursue', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: ThumbsUp, desc: 'Recommended to engage' },
  maybe: { label: 'Maybe', color: 'bg-violet-100 text-violet-700 border-violet-200', icon: AlertCircle, desc: 'Worth considering' },
  skip: { label: 'Skip', color: 'bg-violet-100 text-violet-700 border-violet-200', icon: ThumbsDown, desc: 'Not recommended' },
};

interface ScoreBarProps {
  label: string;
  score: number;
  color: string;
}

const ScoreBar = ({ label, score, color }: ScoreBarProps) => (
  <div className="space-y-1">
    <div className="flex justify-between text-sm">
      <span className="text-stone-600">{label}</span>
      <span className="font-semibold text-stone-900">{score}/100</span>
    </div>
    <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
      <div
        className={`h-full ${color} rounded-full transition-all duration-500`}
        style={{ width: `${score}%` }}
      />
    </div>
  </div>
);

interface PitchDetailData {
  pitch: {
    product_name: string;
    category?: string;
    created_at: string;
    target_company?: { name: string };
    pitch_content: string;
    claims?: string[];
    status: string;
  };
  evaluation?: {
    final_recommendation?: string;
    ai_recommendation?: string;
    relevance_score?: number;
    legitimacy_score?: number;
    fit_score?: number;
    human_override_score?: number;
    overall_score?: number;
    ai_reasoning?: string;
    human_notes?: string;
    ai_evaluated_at?: string;
    reviewed_at?: string;
  };
}

export default function VendorPitchDetailPage() {
  const { pitchId } = useParams<{ pitchId: string }>();
  const { token } = useAuth();
  const [data, setData] = useState<PitchDetailData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchPitchDetails();
  }, [pitchId]);

  const fetchPitchDetails = async () => {
    try {
      const response = await fetch(getApiUrl(`/api/evaluations/pitch/${pitchId}`), {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (!response.ok) {
        throw new Error('Failed to load pitch details');
      }

      const result = await response.json();
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <VendorLayout>
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
        </div>
      </VendorLayout>
    );
  }

  if (error || !data) {
    return (
      <VendorLayout>
        <div className="text-center py-20">
          <AlertCircle className="w-12 h-12 text-violet-400 mx-auto mb-4" />
          <p className="text-stone-600">{error || 'Pitch not found'}</p>
          <Link to="/vendor/pitches" className="text-violet-600 hover:text-violet-700 mt-4 inline-block">
            Back to pitches
          </Link>
        </div>
      </VendorLayout>
    );
  }

  const { pitch, evaluation } = data;
  const statusConfig = STATUS_CONFIG[pitch.status] || STATUS_CONFIG.pending;
  const StatusIcon = statusConfig.icon;

  return (
    <VendorLayout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="pitch-detail-page">
        {/* Back Link */}
        <Link
          to="/vendor/pitches"
          className="inline-flex items-center gap-2 text-stone-600 hover:text-stone-900"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to pitches
        </Link>

        {/* Header */}
        <div className="bg-white rounded-xl border border-stone-200 p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-stone-900">{pitch.product_name}</h1>
              <div className="flex items-center gap-3 mt-2 text-stone-600">
                {pitch.category && (
                  <span className="inline-flex items-center gap-1">
                    <Tag className="w-4 h-4" />
                    {pitch.category}
                  </span>
                )}
                <span className="inline-flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {formatDate(pitch.created_at)}
                </span>
              </div>
            </div>
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${statusConfig.color}`}>
              <StatusIcon className="w-4 h-4" />
              {statusConfig.label}
            </span>
          </div>

          {/* Target Company */}
          {pitch.target_company && (
            <div className="mt-4 pt-4 border-t border-stone-100">
              <p className="text-sm text-stone-500">Pitched to</p>
              <div className="flex items-center gap-2 mt-1">
                <Building2 className="w-4 h-4 text-stone-400" />
                <span className="font-medium text-stone-900">{pitch.target_company.name}</span>
              </div>
            </div>
          )}
        </div>

        {/* Pitch Content */}
        <div className="bg-white rounded-xl border border-stone-200 p-6">
          <h2 className="text-lg font-semibold text-stone-900 mb-4">Pitch Content</h2>
          <div className="prose prose-stone max-w-none">
            <p className="text-stone-700 whitespace-pre-wrap">{pitch.pitch_content}</p>
          </div>

          {pitch.claims && pitch.claims.length > 0 && (
            <div className="mt-6 pt-6 border-t border-stone-100">
              <p className="text-sm font-medium text-stone-700 mb-3">Key Claims</p>
              <div className="flex flex-wrap gap-2">
                {pitch.claims.map((claim, i) => (
                  <span key={i} className="px-3 py-1 bg-violet-50 text-violet-700 rounded-full text-sm">
                    {claim}
                  </span>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Evaluation Results */}
        {evaluation ? (
          <div className="bg-white rounded-xl border border-stone-200 p-6">
            <h2 className="text-lg font-semibold text-stone-900 mb-4">Evaluation Results</h2>

            {/* Recommendation Badge */}
            {(evaluation.final_recommendation || evaluation.ai_recommendation) && (
              <div className="mb-6">
                {(() => {
                  const rec = evaluation.final_recommendation || evaluation.ai_recommendation;
                  const recConfig = rec ? RECOMMENDATION_CONFIG[rec] : null;
                  if (!recConfig) return null;
                  const RecIcon = recConfig.icon;
                  return (
                    <div className={`inline-flex items-center gap-3 px-4 py-3 rounded-xl border ${recConfig.color}`}>
                      <RecIcon className="w-5 h-5" />
                      <div>
                        <p className="font-semibold">{recConfig.label}</p>
                        <p className="text-sm opacity-80">{recConfig.desc}</p>
                      </div>
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Scores */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
              <div className="space-y-4">
                <ScoreBar
                  label="Relevance"
                  score={evaluation.relevance_score || 0}
                  color="bg-violet-500"
                />
                <ScoreBar
                  label="Legitimacy"
                  score={evaluation.legitimacy_score || 0}
                  color="bg-purple-500"
                />
                <ScoreBar
                  label="Fit"
                  score={evaluation.fit_score || 0}
                  color="bg-emerald-500"
                />
              </div>
              <div className="flex items-center justify-center">
                <div className="text-center">
                  <div className="text-5xl font-bold text-stone-900">
                    {evaluation.human_override_score || evaluation.overall_score || 0}
                  </div>
                  <p className="text-stone-500 mt-1">Overall Score</p>
                  {evaluation.human_override_score && (
                    <p className="text-xs text-stone-400 mt-1">Human adjusted</p>
                  )}
                </div>
              </div>
            </div>

            {/* AI Reasoning */}
            {evaluation.ai_reasoning && (
              <div className="pt-6 border-t border-stone-100">
                <p className="text-sm font-medium text-stone-700 mb-2">AI Analysis</p>
                <p className="text-stone-600 text-sm whitespace-pre-wrap">{evaluation.ai_reasoning}</p>
              </div>
            )}

            {/* Human Notes */}
            {evaluation.human_notes && (
              <div className="mt-6 pt-6 border-t border-stone-100">
                <p className="text-sm font-medium text-stone-700 mb-2">Reviewer Notes</p>
                <p className="text-stone-600 text-sm">{evaluation.human_notes}</p>
              </div>
            )}

            {/* Timestamps */}
            <div className="mt-6 pt-6 border-t border-stone-100 flex flex-wrap gap-6 text-xs text-stone-400">
              {evaluation.ai_evaluated_at && (
                <span>AI evaluated: {formatDate(evaluation.ai_evaluated_at)}</span>
              )}
              {evaluation.reviewed_at && (
                <span>Reviewed: {formatDate(evaluation.reviewed_at)}</span>
              )}
            </div>
          </div>
        ) : (
          <div className="bg-violet-50 border border-violet-200 rounded-xl p-6 text-center">
            <Clock className="w-12 h-12 text-violet-400 mx-auto mb-3" />
            <p className="font-medium text-violet-800">Awaiting Evaluation</p>
            <p className="text-sm text-violet-600 mt-1">
              Your pitch is in the queue and will be evaluated by our AI and team shortly.
            </p>
          </div>
        )}
      </div>
    </VendorLayout>
  );
}
